export const DOCUMENT_KINDS = [
	["personal", "Personal"],
	["certificate", "Certificate"],
] as const;

export type EmployeeDocument = { title: string; kind: string; note: string };
export type ParentDetails = { name: string; religion: string; birth: string; nationality: string };
export type EducationRow = { name: string; certificate: string; discipline: string };
export type EmergencyContact = { name: string; relation: string; phone: string };
export type BankDetails = { type: string; number: string; holder: string };
export type PartyDetails = {
	code: string;
	contact: string;
	registration: string;
	address: string;
	city: string;
	state: string;
	postcode: string;
	bank: BankDetails;
};

export type EmployeeProfile = {
	employeeId: string;
	nric: string;
	religion: string;
	gender: string;
	birth: string;
	birthPlace: string;
	nationality: string;
	race: string;
	languages: string;
	marital: string;
	address: string;
	city: string;
	state: string;
	postcode: string;
	mother: ParentDetails;
	father: ParentDetails;
	education: EducationRow[];
	bank: BankDetails;
	emergency: EmergencyContact[];
};

function field(form: FormData, name: string): string {
	return String(form.get(name) ?? "").trim();
}

function blankParent(): ParentDetails {
	return { name: "", religion: "", birth: "", nationality: "" };
}

export function documentKindLabel(kind: string): string {
	return DOCUMENT_KINDS.find(([value]) => value === kind)?.[1] ?? kind;
}

export function documentFromForm(form: FormData): EmployeeDocument {
	return {
		title: field(form, "docTitle"),
		kind: field(form, "docKind") || "personal",
		note: field(form, "docNote"),
	};
}

function parentFromForm(form: FormData, prefix: string): ParentDetails {
	return {
		name: field(form, `${prefix}Name`),
		religion: field(form, `${prefix}Religion`),
		birth: field(form, `${prefix}Birth`),
		nationality: field(form, `${prefix}Nationality`),
	};
}

function rowsFromForm<T>(read: (index: number) => T, filled: (row: T) => boolean): T[] {
	const rows: T[] = [];
	for (let index = 0; index < 12; index += 1) {
		const row = read(index);
		if (filled(row)) rows.push(row);
	}
	return rows;
}

export function profileFromForm(form: FormData): EmployeeProfile {
	return {
		employeeId: field(form, "employeeId"),
		nric: field(form, "nric"),
		religion: field(form, "religion"),
		gender: field(form, "gender"),
		birth: field(form, "birth"),
		birthPlace: field(form, "birthPlace"),
		nationality: field(form, "nationality"),
		race: field(form, "race"),
		languages: field(form, "languages"),
		marital: field(form, "marital"),
		address: field(form, "address"),
		city: field(form, "city"),
		state: field(form, "state"),
		postcode: field(form, "postcode"),
		mother: parentFromForm(form, "mother"),
		father: parentFromForm(form, "father"),
		education: rowsFromForm(
			(index) => ({
				name: field(form, `eduName${index}`),
				certificate: field(form, `eduCert${index}`),
				discipline: field(form, `eduDiscipline${index}`),
			}),
			(row) => Boolean(row.name || row.certificate || row.discipline),
		),
		bank: {
			type: field(form, "bankType"),
			number: field(form, "bankNumber"),
			holder: field(form, "bankHolder"),
		},
		emergency: rowsFromForm(
			(index) => ({
				name: field(form, `emergencyName${index}`),
				relation: field(form, `emergencyRelation${index}`),
				phone: field(form, `emergencyPhone${index}`),
			}),
			(row) => Boolean(row.name || row.relation || row.phone),
		),
	};
}

export function organisationRecord(
	form: FormData,
	kept?: { created_at?: string; documents?: EmployeeDocument[] },
): Record<string, unknown> {
	const kind = field(form, "kind") || "customer";
	const record: Record<string, unknown> = {
		name: field(form, "name"),
		kind,
		phone: field(form, "phone"),
		email: field(form, "email"),
		note: field(form, "note"),
		created_at: kept?.created_at ?? new Date().toISOString(),
	};
	if (kind === "employee") record.profile = profileFromForm(form);
	if (kind === "customer" || kind === "vendor") record.party = partyFromForm(form, kind === "vendor");
	if (kept?.documents?.length) record.documents = kept.documents;
	return record;
}

export function parentOf(value: ParentDetails | string | undefined): ParentDetails {
	if (value && typeof value === "object") return { ...blankParent(), ...value };
	return blankParent();
}

export function partyFromForm(form: FormData, vendor: boolean): PartyDetails {
	return {
		code: field(form, "code"),
		contact: field(form, "contact"),
		registration: vendor ? field(form, "registration") : "",
		address: field(form, "address"),
		city: field(form, "city"),
		state: field(form, "state"),
		postcode: field(form, "postcode"),
		bank: vendor
			? { type: field(form, "bankType"), number: field(form, "bankNumber"), holder: field(form, "bankHolder") }
			: { type: "", number: "", holder: "" },
	};
}

export function partyOf(value: PartyDetails | undefined): PartyDetails {
	const bank = bankOf(value?.bank);
	return {
		code: value?.code ?? "",
		contact: value?.contact ?? "",
		registration: value?.registration ?? "",
		address: value?.address ?? "",
		city: value?.city ?? "",
		state: value?.state ?? "",
		postcode: value?.postcode ?? "",
		bank,
	};
}

export function bankOf(value: BankDetails | string | undefined): BankDetails {
	if (value && typeof value === "object") return { type: value.type ?? "", number: value.number ?? "", holder: value.holder ?? "" };
	return { type: "", number: "", holder: "" };
}
